import type { UIMessage } from "@ai-sdk/react";

export interface ParsedMarkdownTable {
  headers: string[];
  rows: string[][];
  label?: string;
}

export function splitMarkdownTableRow(line: string): string[] {
  let normalized = line.trim();
  if (normalized.startsWith("|")) normalized = normalized.slice(1);
  if (normalized.endsWith("|")) normalized = normalized.slice(0, -1);

  const cells: string[] = [];
  let current = "";
  for (let i = 0; i < normalized.length; i += 1) {
    const char = normalized[i];
    const next = normalized[i + 1];
    if (char === "\\" && next === "|") {
      current += "|";
      i += 1;
      continue;
    }
    if (char === "|") {
      cells.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }
  cells.push(current.trim());
  return cells;
}

export function isMarkdownTableSeparator(line: string): boolean {
  return /^\s*\|?(?:\s*:?-{3,}:?\s*\|)+\s*:?-{3,}:?\s*\|?\s*$/.test(line);
}

export function parseMarkdownTable(
  lines: string[],
  startIndex: number,
): { table: ParsedMarkdownTable; nextIndex: number } | null {
  const headerLine = lines[startIndex];
  const separatorLine = lines[startIndex + 1];
  if (!headerLine || !separatorLine) return null;
  if (!headerLine.includes("|") || !isMarkdownTableSeparator(separatorLine)) return null;

  const headers = splitMarkdownTableRow(headerLine);
  if (headers.length < 2 || headers.some((h) => h.length === 0)) return null;

  const rows: string[][] = [];
  let cursor = startIndex + 2;
  while (cursor < lines.length) {
    const line = lines[cursor];
    if (!line || line.trim().length === 0 || !line.includes("|")) break;
    const cells = splitMarkdownTableRow(line);
    if (cells.length === 0) break;
    if (cells.every((cell) => cell.trim().length === 0)) break;
    rows.push(cells);
    cursor += 1;
  }
  if (rows.length === 0) return null;

  const normalizedRows = rows.map((row) => {
    if (row.length === headers.length) return row;
    if (row.length < headers.length) {
      return [...row, ...Array(headers.length - row.length).fill("")];
    }
    const merged = [...row];
    merged[headers.length - 1] = merged.slice(headers.length - 1).join(" | ");
    return merged.slice(0, headers.length);
  });

  return { table: { headers, rows: normalizedRows }, nextIndex: cursor };
}

export function isImageUrl(value: string): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed.startsWith("http")) return false;
  if (/\.(jpe?g|png|gif|webp|avif|svg)(\?.*)?$/i.test(trimmed)) return true;
  if (/cdn\.shopify\.com/i.test(trimmed)) return true;
  return false;
}

export function isVideoUrl(value: string): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed.startsWith("http")) return false;
  return /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(trimmed);
}

export function messageHasGeneratedMedia(msg: UIMessage): boolean {
  for (const part of msg.parts ?? []) {
    if ((part as { type?: string }).type !== "text") continue;
    const text = (part as { text?: string }).text ?? "";
    if (!text) continue;
    const urls = text.match(/https?:\/\/\S+/g) ?? [];
    for (const u of urls) {
      const cleaned = u.replace(/[).,;\]]+$/, "");
      if (isImageUrl(cleaned) || isVideoUrl(cleaned)) return true;
    }
  }
  return false;
}

export function preprocessChatText(text: string, skipUrls?: Set<string>): string {
  if (!text) return text;
  let out = text;
  out = out.replace(/!\[([^\]]*)\]\s*\n\s*\(([^)\s]+)\)/g, "![$1]($2)");
  out = out.replace(
    /(!\[[^\]]*\]\(([^)]+)\))[ \t]*(?:\r?\n[ \t]*)+URL:[ \t]*\r?\n[ \t]*\2[ \t]*/g,
    "$1",
  );
  out = out.replace(/\r?\n[ \t]*File size:[ \t]*[\d.]+[ \t]*(?:KB|MB|GB|B)\b[^\n]*/gi, "");

  out = out.replace(
    /(?:```|`)(?:json|JSON)?\s*\n([\s\S]*?)\n\s*(?:```|`)/g,
    (_full, body: string) => {
      const media = extractMediaUrlsFromJsonLike(body, skipUrls);
      if (media) return media;
      const status = summarizeGadsStatusJson(body);
      if (status) return `\n${status}\n`;
      return "";
    },
  );

  out = out.replace(
    /^[ \t]*Submitted as\s+\S+\s+[—–-]\s+preset\s+([^,\n]+),\s*(\d+)s,\s*(\S+?)\.\s*Polling\.[ \t]*$/gim,
    "**Job submitted** — preset $1 · $2s · $3",
  );
  out = out.replace(
    /(?:^|\n)((?:[ \t]*~\d+s elapsed,\s*status\s+[a-z_]+\.\s*(?:Continuing|Polling)\.[ \t]*\n?)+)/gim,
    (match) => {
      const lines = match.split(/\r?\n/).filter((l) => l.trim().length > 0);
      const last = lines[lines.length - 1];
      const m = last.match(/~(\d+)s elapsed,\s*status\s+([a-z_]+)/i);
      if (!m) return match;
      const secs = m[1];
      const status = m[2].replace(/_/g, " ");
      const leading = match.startsWith("\n") ? "\n" : "";
      return `${leading}_Generating… ${secs}s elapsed (status: ${status})_\n`;
    },
  );

  out = out.replace(
    /(?<![/.\w])Higgsfield(?:\s+Marketing\s+Studio)?(?![\w.-])/g,
    "Vidzz",
  );
  out = out.replace(/(?<![/.\w])higgsfield(?![\w.-])/g, "vidzz");
  out = out.replace(/\s*\bvia\s+(?:the\s+)?(?:Vidzz\s+)?MCP\b/gi, "");

  out = out.replace(
    /`customers\/\d+\/(campaigns|adGroups|ads|assetGroups|customerAssets)\/(\d+)[^`]*`/g,
    (_m, kind: string, id: string) => `\`${formatResourceLabel(kind)} ${id}\``,
  );
  out = out.replace(
    /\bcustomers\/\d+\/(campaigns|adGroups|ads|assetGroups|customerAssets)\/(\d+)\b/g,
    (_m, kind: string, id: string) => `${formatResourceLabel(kind)} ${id}`,
  );

  out = out.replace(/\n{3,}/g, "\n\n");
  return out;
}

function formatResourceLabel(kind: string): string {
  switch (kind) {
    case "campaigns":
      return "Campaign";
    case "adGroups":
      return "Ad group";
    case "ads":
      return "Ad";
    case "assetGroups":
      return "Asset group";
    case "customerAssets":
      return "Asset";
    default:
      return kind;
  }
}

function summarizeGadsStatusJson(body: string): string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return "";
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return "";
  const rec = parsed as Record<string, unknown>;
  const status = typeof rec.status === "string" ? rec.status : null;
  if (status !== "completed" && status !== "failed") return "";

  const lines: string[] = [];
  if (status === "completed") {
    lines.push("**✓ Task complete**");
    const resource = typeof rec.campaign_resource_name === "string"
      ? rec.campaign_resource_name
      : null;
    if (resource) {
      const id = resource.split("/").pop();
      const kindMatch = resource.match(/\/(campaigns|adGroups|ads|assetGroups)\//);
      const kind = kindMatch ? formatResourceLabel(kindMatch[1]) : "Resource";
      lines.push(`- ${kind}: \`${id}\``);
    }
    if (typeof rec.summary === "string" && rec.summary.trim()) {
      lines.push("");
      lines.push(rec.summary.trim());
    }
  } else {
    lines.push("**✗ Task failed**");
    if (typeof rec.error === "string" && rec.error.trim()) {
      lines.push("");
      lines.push(rec.error.trim());
    }
  }
  return lines.join("\n");
}

function extractMediaUrlsFromJsonLike(blob: string, skipUrls?: Set<string>): string {
  const urls: string[] = [];
  const seen = new Set<string>();
  const shouldSkip = (url: string) => seen.has(url) || (skipUrls?.has(url) ?? false);
  try {
    const parsed = JSON.parse(blob);
    const walk = (node: unknown) => {
      if (!node) return;
      if (typeof node === "string") return;
      if (Array.isArray(node)) {
        node.forEach(walk);
        return;
      }
      if (typeof node === "object") {
        for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
          if (
            (k === "videoUrl" || k === "imageUrl" || k === "url") &&
            typeof v === "string" &&
            /^https?:\/\//i.test(v) &&
            !shouldSkip(v)
          ) {
            seen.add(v);
            urls.push(v);
          } else {
            walk(v);
          }
        }
      }
    };
    walk(parsed);
  } catch {
    // Fall through to regex.
  }
  if (urls.length === 0) {
    const re = /"(?:videoUrl|imageUrl|url)"\s*:\s*"(https?:\/\/[^"\s]+)"/gi;
    let m: RegExpExecArray | null;
    while ((m = re.exec(blob)) !== null) {
      if (!shouldSkip(m[1])) {
        seen.add(m[1]);
        urls.push(m[1]);
      }
    }
  }
  if (urls.length === 0) return "";
  return `\n${urls.join("\n")}\n`;
}

const ATTACHED_MEDIA_REGEX = /\n\n\[(?:Attached images?|Attached media)\]\n([\s\S]*)$/;

export function parseAttachedMediaAnnotation(text: string): {
  displayText: string;
  attachedMediaUrls: string[];
} {
  const match = text.match(ATTACHED_MEDIA_REGEX);
  if (!match) return { displayText: text, attachedMediaUrls: [] };
  return {
    displayText: text.replace(ATTACHED_MEDIA_REGEX, ""),
    attachedMediaUrls: match[1]
      .split("\n")
      .map((url) => url.trim())
      .filter((url) => url.startsWith("http://") || url.startsWith("https://")),
  };
}

export function isProductTable(headers: string[]): boolean {
  const lower = headers.map((h) => h.toLowerCase().trim());
  return (
    lower.includes("title") &&
    (lower.includes("status") || lower.includes("category") || lower.includes("image"))
  );
}

export function findImageHeaderIndex(headers: string[]): number {
  return headers.findIndex((h) => /^image(\s*url)?$/i.test(h.trim()));
}
